import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as adminApi from '../../api/admin'
import type { CrowdMerchantDetail } from '../../types'
import Spinner from '../../components/common/Spinner'
import ShopAnalyticsSection from './ShopAnalyticsSection'
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  SearchInput,
  Select,
  fmtRelative,
  styles,
  tokens,
} from './_ui'

const ROLES: adminApi.MerchantMemberRole[] = ['OWNER', 'ADMIN', 'VIEWER']

const ROLE_TONE: Record<adminApi.MerchantMemberRole, 'green' | 'blue' | 'default'> = {
  OWNER: 'green',
  ADMIN: 'blue',
  VIEWER: 'default',
}

export default function AdminShopDetailPage() {
  const { merchantId = '' } = useParams()
  const navigate = useNavigate()

  const [detail, setDetail] = useState<CrowdMerchantDetail | null>(null)
  const [members, setMembers] = useState<adminApi.MerchantMember[]>([])
  const [membersTotal, setMembersTotal] = useState(0)
  const [membersLoading, setMembersLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [memberSearch, setMemberSearch] = useState('')
  const [memberSearchDebounce, setMemberSearchDebounce] = useState('')
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const [showTransfer, setShowTransfer] = useState(false)
  const [transferTgId, setTransferTgId] = useState('')
  const [transferUsername, setTransferUsername] = useState('')

  const [productsPage, setProductsPage] = useState(0)
  const PRODUCTS_PAGE_SIZE = 12

  const [showAddMember, setShowAddMember] = useState(false)
  const [newTgId, setNewTgId] = useState('')
  const [newUsername, setNewUsername] = useState('')
  const [newRole, setNewRole] = useState<adminApi.MerchantMemberRole>('VIEWER')

  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const loadDetail = async () => {
    if (!merchantId) return
    setLoading(true)
    setError(null)
    try {
      const d = await adminApi.getCrowdMerchantDetail(merchantId)
      setDetail(d as CrowdMerchantDetail)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  const loadMembers = async (append = false) => {
    if (!merchantId) return
    setMembersLoading(true)
    try {
      const data = await adminApi.listMerchantMembers(merchantId, {
        skip: append ? members.length : 0,
        search: memberSearchDebounce || undefined,
      })
      setMembers(append ? [...members, ...data.items] : data.items)
      setMembersTotal(data.total)
    } catch (e) {
      console.error('Failed to load members', e)
    } finally {
      setMembersLoading(false)
    }
  }

  useEffect(() => {
    loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId])

  useEffect(() => {
    const t = setTimeout(() => setMemberSearchDebounce(memberSearch), 300)
    return () => clearTimeout(t)
  }, [memberSearch])

  useEffect(() => {
    loadMembers(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, memberSearchDebounce])

  // Infinite scroll: auto-load when sentinel becomes visible.
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const hasMore = members.length < membersTotal
    if (!hasMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !membersLoading) {
          loadMembers(true)
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members.length, membersTotal, membersLoading])

  const handleTransfer = async () => {
    if (!merchantId) return
    const payload: { telegram_id?: number; telegram_username?: string } = {}
    if (transferTgId.trim()) payload.telegram_id = Number(transferTgId.trim())
    if (transferUsername.trim()) payload.telegram_username = transferUsername.trim().replace(/^@/, '')
    if (!payload.telegram_id && !payload.telegram_username) return
    try {
      await adminApi.assignMerchantOwner(merchantId, payload)
      setShowTransfer(false)
      setTransferTgId('')
      setTransferUsername('')
      await Promise.all([loadDetail(), loadMembers(false)])
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed')
    }
  }

  const handleAddMember = async () => {
    if (!merchantId) return
    const payload: { telegram_id?: number; telegram_username?: string; role: adminApi.MerchantMemberRole } = {
      role: newRole,
    }
    if (newTgId.trim()) payload.telegram_id = Number(newTgId.trim())
    if (newUsername.trim()) payload.telegram_username = newUsername.trim().replace(/^@/, '')
    if (!payload.telegram_id && !payload.telegram_username) return
    try {
      await adminApi.addMerchantMember(merchantId, payload)
      setShowAddMember(false)
      setNewTgId('')
      setNewUsername('')
      setNewRole('VIEWER')
      await Promise.all([loadDetail(), loadMembers(false)])
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed')
    }
  }

  const handleChangeRole = async (memberId: string, role: adminApi.MerchantMemberRole) => {
    if (!merchantId) return
    try {
      await adminApi.updateMerchantMember(merchantId, memberId, role)
      await Promise.all([loadDetail(), loadMembers(false)])
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed')
    }
  }

  const handleRemove = async (memberId: string) => {
    if (!merchantId) return
    if (!confirm('Remove this member?')) return
    try {
      await adminApi.removeMerchantMember(merchantId, memberId)
      await Promise.all([loadDetail(), loadMembers(false)])
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed')
    }
  }

  const handleDeleteShop = async () => {
    if (!detail) return
    if (
      !confirm(
        `Permanently delete "${detail.business_name}" and all its data (products, uploads, members, analytics)?\n\nThis cannot be undone.`,
      )
    )
      return
    setDeleting(true)
    setDeleteError(null)
    try {
      await adminApi.deleteAdminShop(merchantId)
      navigate('/admin/shops')
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete shop')
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div style={{ padding: 16 }}>
        <EmptyState emoji="⚠️" title={error || 'Shop not found'} />
      </div>
    )
  }

  return (
    <div style={{ padding: '8px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        style={{
          background: 'transparent',
          border: 'none',
          color: tokens.text,
          opacity: 0.6,
          fontSize: 13,
          padding: '4px 0',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        ← Back
      </button>

      {/* Hero */}
      <div
        style={{
          background: tokens.cardBg,
          borderRadius: 20,
          padding: 20,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: detail.is_crowd_verified
              ? 'radial-gradient(circle, #22c55e30, transparent 70%)'
              : 'radial-gradient(circle, #6366f130, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', position: 'relative' }}>
          <Avatar src={detail.logo_url} name={detail.business_name} emoji="🏪" size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: tokens.text,
                margin: 0,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              {detail.business_name}
            </h2>
            {detail.slug && (
              <p style={{ ...styles.subtle, fontSize: 12, fontFamily: 'monospace', margin: '4px 0 0' }}>
                /{detail.slug}
              </p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {detail.is_verified && <Badge tone="blue">✓ verified</Badge>}
              {detail.is_crowd_verified && <Badge tone="green">crowd-verified</Badge>}
              {detail.source && <Badge>{detail.source}</Badge>}
              {detail.subscription_tier && (
                <Badge tone={detail.subscription_tier === 'free' ? 'default' : 'amber'}>
                  {detail.subscription_tier}
                </Badge>
              )}
              {(detail.likes_count ?? 0) > 0 && <Badge tone="red">❤ {detail.likes_count}</Badge>}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {detail.address && (
            <p style={{ ...styles.subtle, fontSize: 13, margin: 0 }}>
              📍 {detail.address}
            </p>
          )}
          {detail.contact_phone && (
            <p style={{ ...styles.subtle, fontSize: 13, margin: 0 }}>
              📞 {detail.contact_phone}
            </p>
          )}
          {detail.shop_category && (
            <p style={{ ...styles.subtle, fontSize: 13, margin: 0 }}>
              🏷 {detail.shop_category}
            </p>
          )}
        </div>

        {detail.description && (
          <p style={{ ...styles.subtle, fontSize: 13, marginTop: 10, lineHeight: 1.5 }}>
            {detail.description}
          </p>
        )}

      </div>

      {/* Per-shop statistics — mirrors the merchant analytics page */}
      <ShopAnalyticsSection merchantId={merchantId} />

      {/* Owner */}
      <Card
        title="Primary owner"
        action={
          <Button size="sm" variant="secondary" onClick={() => setShowTransfer(true)}>
            Transfer
          </Button>
        }
      >
        {detail.owner ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              src={detail.owner.photo_url}
              name={[detail.owner.first_name, detail.owner.last_name].filter(Boolean).join(' ') || detail.owner.telegram_username || '?'}
              size={44}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ ...styles.text, fontSize: 14, fontWeight: 600, margin: 0 }}>
                {[detail.owner.first_name, detail.owner.last_name].filter(Boolean).join(' ') ||
                  detail.owner.telegram_username ||
                  '—'}
              </p>
              <p style={{ ...styles.subtle, fontSize: 12, fontFamily: 'monospace', margin: '2px 0 0' }}>
                {detail.owner.telegram_username ? `@${detail.owner.telegram_username} · ` : ''}
                {detail.owner.telegram_id}
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => detail.owner && navigate(`/admin/users/${detail.owner.user_id}`)}
            >
              View →
            </Button>
          </div>
        ) : (
          <p style={{ ...styles.subtle, fontSize: 13, margin: 0 }}>No owner attached</p>
        )}
      </Card>

      {/* Members */}
      <Card
        title={`Members · ${membersTotal}`}
        action={
          <Button size="sm" onClick={() => setShowAddMember(true)}>
            + Add
          </Button>
        }
      >
        <div style={{ marginBottom: 10 }}>
          <SearchInput
            value={memberSearch}
            onChange={setMemberSearch}
            placeholder="Name, @username or telegram id…"
          />
        </div>
        {members.length === 0 && !membersLoading ? (
          <EmptyState
            emoji="👥"
            title={memberSearchDebounce ? 'No members match' : 'No members yet'}
            subtitle={memberSearchDebounce ? 'Try a different search' : 'Add collaborators to this shop'}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {members.map((m) => {
              const isOwner = m.user_id === detail.owner?.user_id
              return (
                <div
                  key={m.id}
                  style={{
                    ...styles.innerTile,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <Avatar
                    src={m.photo_url}
                    name={[m.first_name, m.last_name].filter(Boolean).join(' ') || m.telegram_username || '?'}
                    size={36}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <p
                        style={{
                          ...styles.text,
                          fontSize: 13,
                          fontWeight: 600,
                          margin: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {[m.first_name, m.last_name].filter(Boolean).join(' ') ||
                          m.telegram_username ||
                          '—'}
                      </p>
                      <Badge tone={ROLE_TONE[m.role]}>{m.role}</Badge>
                    </div>
                    <p style={{ ...styles.subtle, fontSize: 11, fontFamily: 'monospace', margin: '2px 0 0' }}>
                      {m.telegram_username ? `@${m.telegram_username} · ` : ''}
                      {m.telegram_id}
                      {m.created_at && ` · joined ${fmtRelative(m.created_at)}`}
                    </p>
                  </div>
                  {!isOwner && (
                    <>
                      <select
                        value={m.role}
                        onChange={(e) =>
                          handleChangeRole(m.id, e.target.value as adminApi.MerchantMemberRole)
                        }
                        style={{
                          background: tokens.cardBg,
                          color: tokens.text,
                          fontSize: 11,
                          fontWeight: 600,
                          border: 'none',
                          borderRadius: 8,
                          padding: '6px 8px',
                          cursor: 'pointer',
                        }}
                      >
                        {ROLES.filter((r) => r !== 'OWNER').map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleRemove(m.id)}
                        title="Remove"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: tokens.red,
                          opacity: 0.7,
                          fontSize: 16,
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              )
            })}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {membersLoading && (
              <div style={{ ...styles.subtle, fontSize: 12, textAlign: 'center', padding: '8px 0' }}>
                Loading…
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Products */}
      <Card title={`Products · ${detail.products.length}`}>
        {detail.products.length === 0 ? (
          <EmptyState emoji="📦" title="No products yet" />
        ) : (() => {
          const totalPages = Math.max(1, Math.ceil(detail.products.length / PRODUCTS_PAGE_SIZE))
          const page = Math.min(productsPage, totalPages - 1)
          const start = page * PRODUCTS_PAGE_SIZE
          const visible = detail.products.slice(start, start + PRODUCTS_PAGE_SIZE)
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 6,
                }}
              >
                {visible.map((p) => {
                  const src = p.medium_url || p.thumbnail_url
                  return (
                    <div
                      key={p.id}
                      style={{
                        background: tokens.pageBg,
                        borderRadius: 10,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {src ? (
                        <img
                          src={src}
                          alt=""
                          style={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            background: tokens.cardBg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 22,
                            opacity: 0.4,
                          }}
                        >
                          📦
                        </div>
                      )}
                      <div style={{ padding: '6px 8px' }}>
                        <p
                          style={{
                            ...styles.text,
                            fontSize: 11,
                            fontWeight: 600,
                            margin: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.25,
                          }}
                        >
                          {p.name}
                        </p>
                        {p.price != null && (
                          <p style={{ ...styles.subtle, fontSize: 10, margin: '2px 0 0' }}>
                            {p.price.toLocaleString()} {p.price_currency || ''}
                          </p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    marginTop: 10,
                  }}
                >
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setProductsPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    ← Prev
                  </Button>
                  <span style={{ ...styles.subtle, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                    Page {page + 1} / {totalPages}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setProductsPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next →
                  </Button>
                </div>
              )}
            </>
          )
        })()}
      </Card>

      {/* Danger zone */}
      <Card title="Danger zone">
        <p style={{ ...styles.subtle, fontSize: 12, margin: '0 0 12px' }}>
          Permanently delete this shop and all its data — products, uploads, members, and
          analytics included. This cannot be undone.
        </p>
        {deleteError && (
          <p style={{ color: tokens.red, fontSize: 12, margin: '0 0 10px' }}>{deleteError}</p>
        )}
        <Button variant="danger" onClick={handleDeleteShop} disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete shop'}
        </Button>
      </Card>

      {showTransfer && (
        <Modal title="Transfer ownership" onClose={() => setShowTransfer(false)}>
          <p style={{ ...styles.subtle, fontSize: 12, marginTop: -4, marginBottom: 14 }}>
            The new owner must have signed into the platform at least once.
          </p>
          <Field
            label="Telegram ID"
            value={transferTgId}
            onChange={setTransferTgId}
            placeholder="123456789"
            inputMode="numeric"
          />
          <Field
            label="or Username"
            value={transferUsername}
            onChange={setTransferUsername}
            placeholder="username"
          />
          <Button onClick={handleTransfer} style={{ width: '100%' }}>
            Transfer ownership
          </Button>
        </Modal>
      )}

      {showAddMember && (
        <Modal title="Add member" onClose={() => setShowAddMember(false)}>
          <Field
            label="Telegram ID"
            value={newTgId}
            onChange={setNewTgId}
            placeholder="123456789"
            inputMode="numeric"
          />
          <Field
            label="or Username"
            value={newUsername}
            onChange={setNewUsername}
            placeholder="username"
          />
          <Select<adminApi.MerchantMemberRole>
            label="Role"
            value={newRole}
            onChange={setNewRole}
            options={ROLES.filter((r) => r !== 'OWNER')}
          />
          <Button onClick={handleAddMember} style={{ width: '100%' }}>
            Add member
          </Button>
        </Modal>
      )}
    </div>
  )
}
